import { Col, DatePicker, Form, Input, message, Modal, Row, Select, Switch, Upload } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { addReceipts, getallProject, getallProjectForReceipts, getAllReceipts, getCustomers, getCustomersForReceipts, getLastReceiptNumber } from "../../utils/API";
import axios from "axios";
import { AuthContext } from "../../Auth/AuthContext";

interface PrefillData {
    customer_id?: string;
    project_id?: string;
    amount?: number;
    description?: string;
    authorize?: boolean;
}

interface AddReceiptsProps {
    visible: boolean;
    onClose: () => void;
    onTaskCreated: () => void;
    prefillData?: PrefillData;
}

interface ReceiptsFormValues {
    _id: string;
    receipts_number: string;
    project: string;
    customer: string;
    amount: number;
    method: string;
    bank: string;
    payment_date: string;
    collected_by: string;
    authorize: boolean;
    description: string;
}

const AddReceiptsModel: React.FC<AddReceiptsProps> = ({
    visible,
    onClose,
    onTaskCreated,
    prefillData,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [customers, setCustomers] = useState<any>([]);
    const [projects, setProjects] = useState<any>([]);
    const { authData } = useContext(AuthContext);
    const [receiptSeq, setReceiptSeq] = useState(1);

    const role_id = authData.roleId;

    const handleCancel = () => {
        form.resetFields();
        onClose();
    }

    // Auto generate Receipts Number
    const prefix = "RCV";
    const year = dayjs().format("YYYY");

    const formatSequence = (num: number) => num.toString().padStart(4, "0");

    const generateReceiptNumber = (seq: number) => {
        return `${prefix}-${year}-${formatSequence(seq)}`;
    };

    const onFinish = async (values: ReceiptsFormValues) => {
        setIsLoading(true);
        try {
            const formData = new FormData();

            const finalValues = {
                ...values,
            };

            Object.entries(finalValues).forEach(([key, value]) => {
                if (key === 'payment_date' && value && typeof value === 'object' && 'format' in value) {
                    formData.append(key, (value as any).format('DD-MM-YYYY'));
                } else if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });

            // console.log("Form Data: ", formData);
            await addReceipts(formData);

            getAllReceipts("");
            message.success("Receipts Added Successfully!");
            handleCancel();
            onTaskCreated();
        } catch (error) {
            console.error('Error submitting Receipts:', error);
            if (axios.isAxiosError(error)) {
                message.error(error.response?.data?.message || 'An error occurred.');
            } else {
                message.error('An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchCustomers = async () => {
            setIsLoading(true);
            try {
                const response: any = await getCustomersForReceipts("", "", true);

                if (response?.data) {
                    // Filter only active clients
                    const activeCustomers = response.data.filter(
                        (client: any) => client.isActive === true
                    );
                    setCustomers(activeCustomers);
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchReceiptsNumber = async () => {
            try {
                const response = await getLastReceiptNumber();

                // 🔥 FIRST ENTRY CASE
                if (!response?.receipts_number) {
                    setReceiptSeq(1);
                    return;
                }

                // Example: RCV-2025-0042
                const parts = response.receipts_number.split("-");
                const lastSeq = parseInt(parts[2], 10);

                if (isNaN(lastSeq)) {
                    setReceiptSeq(1);
                } else {
                    setReceiptSeq(lastSeq + 1);
                }
            } catch (error) {
                console.error("Error fetching receipt number:", error);
                setReceiptSeq(1); // fallback safe default
            }
        };

        const fetchProjects = async () => {
            setIsLoading(true);
            try {
                const response: any = await getallProjectForReceipts("", "", true);

                if (response?.data) {
                    const activeProjects = response.data.filter(
                        (client: any) => client.isActive === true
                    );
                    setProjects(activeProjects);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchCustomers();
        fetchReceiptsNumber();
        fetchProjects();
    }, [visible, receiptSeq]);

    useEffect(() => {
        if (visible) {
            const receiptNumber = generateReceiptNumber(receiptSeq);

            form.setFieldsValue({
                receipts_number: receiptNumber,
                payment_date: dayjs(),
                authorize: prefillData?.authorize !== undefined ? prefillData.authorize : (role_id.roleId === 1 ? true : false),
                ...(prefillData?.amount !== undefined && { amount: prefillData.amount }),
                ...(prefillData?.description && { description: prefillData.description }),
            });
        }
    }, [visible]);

    useEffect(() => {
        if (visible && prefillData && (customers.length > 0 || projects.length > 0)) {
            const patch: Record<string, any> = {};

            if (prefillData.customer_id && customers.length > 0) {
                const found = customers.find((c: any) => c._id === prefillData.customer_id);
                if (found) patch.customer_name = prefillData.customer_id;
            }

            if (prefillData.project_id && projects.length > 0) {
                const found = projects.find((p: any) => p._id === prefillData.project_id);
                if (found) {
                    patch.project = prefillData.project_id;
                } else if (prefillData.customer_id) {
                    const matched = projects.find((p: any) => p.clientId?._id === prefillData.customer_id);
                    if (matched) patch.project = matched._id;
                }
            } else if (prefillData.customer_id && projects.length > 0) {
                const matched = projects.find((p: any) => p.clientId?._id === prefillData.customer_id);
                if (matched) patch.project = matched._id;
            }

            if (Object.keys(patch).length > 0) form.setFieldsValue(patch);
        }
    }, [visible, prefillData, customers, projects]);

    // Watch the currently selected project
    const selectedProjectId = Form.useWatch("project", form);
    const selectedCustomerId = Form.useWatch("customer_name", form);

    // Find the project object
    const selectedProject = projects.find((p: any) => p._id === selectedProjectId);
    const selectedCustomer = customers.find((c: any) => c._id === selectedCustomerId);

    // Filter customers: only show the customer linked to selected project
    const filteredCustomers = selectedProject
        ? customers.filter((c: any) => c._id === selectedProject.clientId?._id)
        : [];

    const filteredProjects = selectedCustomer
        ? projects.filter((p: any) => p.clientId?._id === selectedCustomer._id)
        : [];

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={760}
            centered
            destroyOnClose
            className="custom-modal receipt-form-modal"
            style={{ maxWidth: "calc(100vw - 32px)" }}
            styles={{
                header: {
                    padding: "14px 20px",
                    margin: 0,
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #eef2f7",
                    zIndex: 2,
                },
                body: {
                    fontFamily: "inter, sans-serif",
                    padding: "8px 20px",
                    maxHeight: "calc(100vh - 60px)",
                    overflowY: "auto",
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                    borderRadius: 12,
                    boxShadow: "0 12px 40px rgba(15,23,42,0.18)",
                },
            }}
            title={
                <div
                    style={{
                        color: "#1f2937",
                        fontFamily: "inter, sans-serif",
                        fontSize: "18px",
                        fontWeight: 600,
                    }}
                >
                    Add Receipts
                </div>
            }
        >
            <Form onFinish={onFinish} form={form} layout="horizontal"
                labelCol={{ flex: "160px" }}
                wrapperCol={{ flex: "1 1 auto" }}
                colon={false} style={{ margin: 0 }}>
                <div className="elements">
                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Project
                            </span>
                        }
                        name="project"
                        required={false}
                    >
                        <Select
                            size="large"
                            // disabled={roleId === 2}
                            loading={isLoading}
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select Project"
                            className="CustomSelect"
                            onChange={(projectId) => {
                                const selectedProject = projects.find(
                                    (p: any) => p._id === projectId
                                );

                                if (selectedProject?.clientId?._id) {
                                    form.setFieldsValue({
                                        customer_name: selectedProject.clientId?._id
                                    });
                                }
                            }}
                        >
                            {projects.map((project: any) => (
                                <Select.Option key={project._id} value={project._id}>
                                    {project.projectName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Customer Name
                            </span>
                        }
                        name="customer_name"
                        required={false}
                    >
                        <Select
                            size="large"
                            // disabled={roleId === 2}
                            loading={isLoading}
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select Customer Name"
                            className="CustomSelect"
                            onChange={(customerId) => {
                                const relatedProject = projects.find(
                                    (p: any) => p.clientId?._id === customerId
                                );

                                if (relatedProject) {
                                    form.setFieldsValue({
                                        project: relatedProject._id,
                                    });
                                } else {
                                    form.setFieldsValue({
                                        project: null,
                                    });
                                }
                            }}
                        >
                            {customers.map((customer: any) => (
                                <Select.Option key={customer._id} value={customer._id}>
                                    {customer.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Receipts Number
                            </span>
                        }
                        name="receipts_number"
                    >
                        <Row gutter={8}>
                            <Col flex="80px">
                                <Input value={prefix} className="Allinput text-[14px]" />
                            </Col>
                            <Col flex="80px">
                                <Input value={year} className="Allinput text-[14px]" />
                            </Col>
                            <Col flex="auto">
                                <Input
                                    value={formatSequence(receiptSeq)}
                                    className="Allinput"
                                />
                            </Col>
                        </Row>
                    </Form.Item>

                    <Form.Item
                        initialValue={dayjs()}
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Payment Date
                            </span>
                        }
                        name="payment_date"
                        required={false}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Amount
                            </span>
                        }
                        name="amount"
                        required={false}
                    >
                        <Input type="Number" placeholder="Amount" className="Allinput" />
                    </Form.Item>

                    <Form.Item
                        name="method"
                        initialValue="Bank"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Method
                            </span>
                        }
                        required={false}
                    >
                        <Select
                            placeholder="Select Method"
                            className="CustomSelect"
                            showSearch
                            optionFilterProp="children"
                            // value={position}
                            onChange={(value) => {
                                // setPosition(value);
                                form.setFieldsValue({ technology: undefined });
                            }}
                        >
                            <Select.Option value="Bank">Bank</Select.Option>
                            <Select.Option value="Cash">Cash</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="collected_by"
                        initialValue="System"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Collected By
                            </span>
                        }
                        required={false}
                    >
                        <Select
                            placeholder="Select Collected By"
                            className="CustomSelect"
                            // value={position}
                            onChange={(value) => {
                                // setPosition(value);
                                form.setFieldsValue({ technology: undefined });
                            }}
                        >
                            <Select.Option value="System">System</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="authorize"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Authorized?
                            </span>
                        }
                        valuePropName="checked"     // 👈 important for Switch
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Description <span className="text-red-500">*</span>
                            </span>
                        }
                        name="description"
                        required={false}
                        rules={[{ required: true, message: "Please enter the description" }]}
                    >
                        <Input.TextArea
                            placeholder="Enter description..."
                            className="Allinputtextarea"
                        />
                    </Form.Item>
                </div>
                <Row justify="end">
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        xl={24}
                        xxl={20}
                        className="d-flex justify-content-end me-4"
                    >
                        <div className="button-wrapper">
                            <button
                                className="allcanclebutton"
                                onClick={onClose}
                                type="button"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="allsubmitebutton w-20"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default AddReceiptsModel;